import React, { useState } from 'react';
import Icon from '@avtechsoftware/react-component-library/src/components/common/Icon/Icon';
import axios from 'axios';
import moment from 'moment';
import PropTypes from 'prop-types';
import ProgressSpinner from '@avtechsoftware/react-component-library/src/components/common/ProgressSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AxiosDataWrapper
  from '@avtechsoftware/react-component-library/src/components/common/AxiosDataWrapper/AxiosDataWrapper';
import AxiosDataIsLoaded
  from '@avtechsoftware/react-component-library/src/components/common/AxiosDataWrapper/AxiosDataIsLoaded/AxiosDataIsLoaded';
import { useSelector } from 'react-redux';
import SubscriptionNavTabs from '../SubscriptionNavTabs/SubscriptionNavTabs';
import BalanceButtonsGroup from '../BalanceButtonsGroup/BalanceButtonsGroup';
import SmsInfoBlurb from '../SmsInfoBlurb/SmsInfoBlurb';
import AddFundsForm from '../AddFundsForm/AddFundsForm';
import { connectModalContainerContextToComponent } from '../../../contexts/ModalContext/ModalContext';
import formatFinancial from '../../../utility/formatFinancial';
import { RaaRoles, sessionUserHasRolePrivileges } from '../../../utility/SecurityRules';
import HasRolePrivileges from '../../highorder/HasRolePrivileges';
import SmsPricingModal from '../SmsPricingModal/SmsPricingModal';
import { currentMonth, currentYear, getDateDropDownOptionsMonthAndYear } from '../../../utility/DateOptionUtility';
import { selectSessionInformation } from '../../../redux/slices/sessionSlice';
import convertNumberToFinancialString from '../../../utility/convertNumberToFinancialString';

const transactionTypesMap = {
  1: 'Paid',
  2: 'Adjustment',
  3: 'Message',
  4: 'Test',
  5: 'Zero Balance',
  6: 'Skip',
  7: 'Blocked',
};
const balanceTypesMap = {
  1: 'Deposit',
  2: 'Adjustment',
  3: 'SMS message',
  4: 'Test',
  5: 'Zero Balance',
  6: 'Skip',
  7: 'Blocked',
};

function SmsBalancePage(props) {
  const {
    setModalJSX,
  } = props;
  const sessionInformation = useSelector(selectSessionInformation);
  const [balance, setBalance] = useState(null);
  const [balanceDetails, setBalanceDetails] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedDate, setSelectedDate] = useState(`${currentMonth + 1}-${currentYear}`);
  const [isFundsFormOpen, setIsFundsFormOpen] = useState(false);
  const [isSearchingTransactions, setIsSearchingTransactions] = useState(true);
  const [prices, setPrices] = useState([]);
  const { activeSubscription, timezone } = sessionInformation;
  const hasAdvancedAlerting = activeSubscription.activeTierHasAdvancedAlerting;
  const userCanAddFunds = sessionUserHasRolePrivileges(
    activeSubscription, [RaaRoles.BILLING, RaaRoles.ADMIN],
  );
  const filteredTransaction = transactions.filter((transaction) => transaction.typeId === '1');

  React.useEffect(() => {
    axios.get('/sms/balance')
      .then((r) => {
        setBalance(r.data);
      })
      // eslint-disable-next-line no-console
      .catch((e) => console.log(e));
  }, []);

  React.useEffect(() => {
    axios.post('/sms/pricing')
      .then((r) => {
        setPrices(r.data.countryPricingList);
      })
      // eslint-disable-next-line no-console
      .catch((e) => console.log(e));
  }, []);

  React.useEffect(() => {
    setIsSearchingTransactions(true);
    const params = new URLSearchParams();
    params.append('month', selectedDate.split('-')[0]);
    params.append('year', selectedDate.split('-')[1]);
    axios.post('/sms/transaction-list', params)
      .then((r) => {
        const { data } = r;
        setBalanceDetails(data);
        setTransactions(data);
        setIsSearchingTransactions(false);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.log(e);
        setIsSearchingTransactions(false);
      });
  }, [selectedDate]);

  const handleOpenAddFundsForm = () => setIsFundsFormOpen(true);
  /* istanbul ignore next */
  const handleOpenPricingModal = () => {
    const jsx = (
      <SmsPricingModal prices={prices} setModalJSX={setModalJSX} />
    );
    setModalJSX(jsx);
  };
  /* istanbul ignore next */
  const handleDateSelect = (e) => {
    const { value } = e.target;
    setSelectedDate(value);
  };

  const renderTimestampWithTimeZone = (timestamp, timeZone) => {
    if (timestamp === null || timestamp === 0) {
      return 'N/A';
    }
    const date = new Date(timestamp);
    const display = timeZone ? `${date.toLocaleString('en-US', {
      timeZone,
      timeZoneName: 'short',
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    })}` : `${date.toLocaleString()}`;
    return display;
  };
  /* istanbul ignore next */
  return (
    <>
      <SubscriptionNavTabs activeKey="/s/subscription/sms" />

      {
                isFundsFormOpen ? (
                  <AxiosDataWrapper
                    url="/subscription/billing/credit-card"
                    config={{ dataTitle: 'creditCardOnFile' }}
                  >
                    <AxiosDataIsLoaded>
                      <AddFundsForm
                        setIsFundsFormOpen={setIsFundsFormOpen}
                        balance={balance}
                      />
                    </AxiosDataIsLoaded>
                  </AxiosDataWrapper>
                )
                  : (
                    <div className="container">
                      <SmsInfoBlurb hasAdvancedAlerting={hasAdvancedAlerting} />
                      <div className="clearfix text-right my-3">
                        <h5 className="float-left">SMS Balance</h5>
                      </div>
                      <BalanceButtonsGroup
                        hasAdvancedAlerting={hasAdvancedAlerting}
                        balance={balance}
                        userCanAddFunds={userCanAddFunds}
                        handleOpenForm={handleOpenAddFundsForm}
                        handleOpenModal={handleOpenPricingModal}
                      />
                      {
                          filteredTransaction.length > 0
                            && (
                            <HasRolePrivileges
                              targetRoleList={[RaaRoles.BILLING, RaaRoles.ADMIN]}
                            >
                              <div className="clearfix text-right mt-3">
                                <h5 className="float-left">Payments</h5>
                              </div>
                              <div className="table-responsive">
                                <table className="table table-sm table-striped mb-5">
                                  <thead>
                                    <tr>
                                      <th scope="col">Date</th>
                                      <th scope="col">Description</th>
                                      <th scope="col">Status</th>
                                      <th scope="col">SMS</th>
                                      <th scope="col">Tax</th>
                                      <th scope="col">Total</th>
                                      {/* eslint-disable-next-line max-len */}
                                      {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                                      <th scope="col" />
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {
                                      filteredTransaction.map((payment, index) => {
                                        // eslint-disable-next-line max-len
                                        const context = JSON.parse(JSON.parse(JSON.stringify(payment.context)));
                                        const isAmountANumber = context.amount ? /^-?\d+$/.test(context.amount) : false;
                                        const amount = isAmountANumber
                                          ? formatFinancial(context.amount)
                                          : '';
                                        return (
                                          <tr key={index}>
                                            <td>{moment(payment.timestamp).format('MMM DD, YYYY')}</td>
                                            <td>
                                              {context.description}
                                            </td>
                                            <td>{transactionTypesMap[payment.typeId]}</td>
                                            <td>
                                              {`${amount !== '' ? '$' : ''}${amount}`}
                                            </td>
                                            <td>
                                              {/* eslint-disable-next-line max-len */}
                                              {convertNumberToFinancialString(parseFloat(payment.tax))}
                                            </td>
                                            <td>
                                              {/* eslint-disable-next-line max-len */}
                                              {convertNumberToFinancialString(parseFloat(payment.tax) + parseFloat(amount))}
                                            </td>
                                            <td>
                                              <span style={{ color: '#007bff' }}>
                                                <a href={`/subscription/billing/requested-sms-invoice/${payment.id}`}>
                                                  Invoice
                                                  <Icon icon="file-alt" className="ml-1" />
                                                </a>
                                              </span>
                                            </td>
                                          </tr>
                                        );
                                      })
                                    }
                                  </tbody>
                                </table>
                              </div>
                            </HasRolePrivileges>
                            )
                          }

                      <div className="clearfix my-3">
                        <h5 className="float-left mb-0 pt-2">Balance Details</h5>
                        <div className="float-right form-group row">
                          <div className="form-inline col flex-nowrap">
                            <select
                              className="form-control w-auto"
                              value={selectedDate}
                              onChange={handleDateSelect}
                            >
                              {getDateDropDownOptionsMonthAndYear(2023).map((date, index) => (
                                <option
                                  key={`date${index}`}
                                  value={date.value}
                                >
                                  {date.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col pl-0">
                            <a href={`/sms/csv?month=${selectedDate.split('-')[0]}&year=${selectedDate.split('-')[1]}`}>
                              <FontAwesomeIcon
                                icon="file-csv"
                                size="lg"
                                className="mt-2"
                                style={{ verticalAlign: 'middle' }}
                              />
                            </a>
                            {' '}
                          </div>
                        </div>
                      </div>
                      {!isSearchingTransactions
                        ? (
                          <div className="table-responsive">
                            <table className="table table-sm table-striped mb-5">
                              <thead>
                                <tr>
                                  <th scope="col">Timestamp</th>
                                  <th scope="col">Type</th>
                                  <th scope="col">Country</th>
                                  <th scope="col">Number</th>
                                  <th scope="col">Amount</th>
                                  <th scope="col">Balance</th>
                                </tr>
                              </thead>
                              <tbody>
                                {balanceDetails.length > 0
                                    && balanceDetails.map((entry, index) => {
                                      // eslint-disable-next-line max-len
                                      const detailTime = renderTimestampWithTimeZone(entry.timestamp, timezone);
                                      // eslint-disable-next-line max-len
                                      const context = !!entry.context && JSON.parse(JSON.parse(JSON.stringify(entry.context)));
                                      let amount = entry.smsMetadata !== null ? entry.smsMetadata.raPrice : '';
                                      if (entry.typeId === '1' || entry.typeId === '2') {
                                        const balanceAmount = !!context.amount && context.amount;
                                        const isAmountANumber = /^-?\d+$/.test(balanceAmount);
                                        amount = isAmountANumber
                                          ? formatFinancial(balanceAmount)
                                          : amount;
                                      }
                                      if (entry.typeId === '7' || entry.typeId === '4') {
                                        amount = formatFinancial(0);
                                      }

                                      return (
                                        <tr key={index}>
                                          <td>{detailTime}</td>
                                          <td>{balanceTypesMap[entry.typeId]}</td>
                                          {/* eslint-disable-next-line max-len */}
                                          <td>{entry.smsMetadata !== null && entry.smsMetadata.country.displayName}</td>
                                          <td>{entry.smsNumber}</td>
                                          <td>
                                            {`${entry.typeId === '3' && amount !== '' ? '-' : ''}${amount !== '' ? '$' : ''}${amount}`}
                                          </td>
                                          <td>{entry.balance}</td>
                                        </tr>
                                      );
                                    })}
                              </tbody>
                            </table>
                          </div>
                        )
                        : (
                          <div className="row d-flex justify-content-center my-5">
                            <div>
                              <ProgressSpinner progressText="" />
                            </div>
                          </div>
                        )}
                    </div>
                  )
            }

    </>
  );
}

SmsBalancePage.propTypes = {
  setModalJSX: PropTypes.func.isRequired,
};

export { SmsBalancePage };
export default connectModalContainerContextToComponent(
  SmsBalancePage,
);
